import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import { Label } from '../components/ui/label';
import { CurrencyInput } from '../components/ui/currency-input';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  Home,
  ChevronLeft,
  Loader2,
  Upload,
  X,
  MapPin,
  DollarSign,
  Building,
  Bed,
  Bath,
  Maximize,
  Globe,
  GraduationCap,
  Shield,
  Mail,
  Phone,
  MessageCircle,
  Calendar
} from 'lucide-react';
import { getCurrencyForCountry } from '../utils/currencyMapping';

const API = ''; // Use relative URL for same-origin API calls

const CreatePropertyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const isEditMode = !!propertyId;
  const isIOSApp = useIsIOSApp();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState({
    property_types: [
      'Apartment', 'House', 'Studio', 'Room', 'Shared Room', 'Villa', 
      'Penthouse', 'Loft', 'Townhouse', 'Other'
    ],
    listing_types: ['rent', 'sale', 'short-term'],
    amenities: [
      'WiFi', 'Air Conditioning', 'Heating', 'Washer', 'Dryer', 'Dishwasher',
      'Parking', 'Gym', 'Pool', 'Elevator', 'Balcony', 'Garden', 'Pet Friendly',
      'Furnished', 'Unfurnished', 'Security', 'Storage'
    ]
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: '',
    listing_type: 'rent',
    address: '',
    district: '',
    city: '',
    country: '',
    bedrooms: 1,
    bathrooms: 1,
    size_sqm: '',
    size_sqft: '',
    floor: '',
    total_floors: '',
    year_built: '',
    price: '',
    price_currency: 'USD',
    price_period: 'month',
    deposit: '',
    deposit_currency: 'USD',
    amenities: [],
    images: [],
    is_expat_friendly: true,
    near_international_schools: false,
    near_expat_areas: false,
    visa_assistance: false,
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_whatsapp: '',
    available_from: ''
  });

  useEffect(() => {
    if (!user) {
      toast.error('Please sign in to list a property');
      navigate('/');
      return;
    }
    fetchCategories();
    if (isEditMode) {
      fetchPropertyData();
    }
  }, [user, navigate]);

  const fetchPropertyData = async () => {
    try {
      const response = await axios.get(`${API}/api/housing/${propertyId}`);
      const p = response.data;
      setFormData({
        title: p.title || '',
        description: p.description || '',
        property_type: p.property_type || '',
        listing_type: p.listing_type || 'rent',
        address: p.address || '',
        district: p.district || '',
        city: p.city || '',
        country: p.country || '',
        bedrooms: p.bedrooms || 1,
        bathrooms: p.bathrooms || 1,
        size_sqm: p.size_sqm || '',
        size_sqft: p.size_sqft || '',
        floor: p.floor || '',
        total_floors: p.total_floors || '',
        year_built: p.year_built || '',
        price: p.price || '',
        price_currency: p.price_currency || 'USD',
        price_period: p.price_period || 'month',
        deposit: p.deposit || '',
        deposit_currency: p.deposit_currency || 'USD',
        amenities: p.amenities || [],
        images: p.images || [],
        is_expat_friendly: p.is_expat_friendly ?? true,
        near_international_schools: p.near_international_schools || false,
        near_expat_areas: p.near_expat_areas || false,
        visa_assistance: p.visa_assistance || false,
        contact_name: p.contact_name || '',
        contact_email: p.contact_email || '',
        contact_phone: p.contact_phone || '',
        contact_whatsapp: p.contact_whatsapp || '',
        available_from: p.available_from || ''
      });
    } catch (error) {
      toast.error('Failed to load property data');
      navigate('/housing');
    }
  };

  // Auto-select currency based on country
  useEffect(() => {
    if (formData.country) {
      const currency = getCurrencyForCountry(formData.country);
      if (currency) {
        setFormData(prev => ({ ...prev, price_currency: currency }));
      }
    }
  }, [formData.country]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/housing/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.property_type || !formData.city || !formData.country || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        deposit: formData.deposit ? parseFloat(formData.deposit) : null,
        deposit_currency: formData.deposit_currency,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        size_sqm: formData.size_sqm ? parseFloat(formData.size_sqm) : null,
        size_sqft: formData.size_sqft ? parseFloat(formData.size_sqft) : null,
        floor: formData.floor ? parseInt(formData.floor) : null,
        total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null
      };

      if (isEditMode) {
        await axios.put(`${API}/api/housing/${propertyId}`, submitData);
        toast.success('Property listing updated!');
        navigate(`/housing/${propertyId}`);
      } else {
        await axios.post(`${API}/api/housing`, submitData);
        toast.success('Property listed successfully! It will be visible after admin approval.');
        navigate('/housing');
      }
    } catch (error) {
      // Handle Pydantic validation errors (422) which return an array
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create listing';
      
      if (Array.isArray(detail)) {
        errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (typeof detail === 'object' && detail?.msg) {
        errorMessage = detail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/housing')}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to listings
            </button>
            <h1 className="text-3xl font-black">{isEditMode ? 'Edit Property' : 'List Your Property'}</h1>
            <p className="text-muted-foreground">{isEditMode ? 'Update your property listing details' : 'Create a new property listing for expats'}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Basic Information
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Property Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Modern 2BR Apartment in Downtown"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your property in detail..."
                    className="mt-1 min-h-[120px]"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Listing Type *</Label>
                    <Select
                      value={formData.listing_type}
                      onValueChange={(value) => setFormData({ ...formData, listing_type: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rent">For Rent</SelectItem>
                        <SelectItem value="sale">For Sale</SelectItem>
                        <SelectItem value="short-term">Short-term Rental</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Property Type *</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.property_types?.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Location
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="e.g., 123 Main Street"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="district">District / Neighborhood</Label>
                  <Input
                    id="district"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    placeholder="e.g., Shibuya, Manhattan, etc."
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Tokyo"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="e.g., Japan"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Home className="w-5 h-5 text-primary" />
                Property Details
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    min="0"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    min="0"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="size_sqm">Size (sqm)</Label>
                  <Input
                    id="size_sqm"
                    type="number"
                    value={formData.size_sqm}
                    onChange={(e) => setFormData({ ...formData, size_sqm: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="floor">Floor</Label>
                  <Input
                    id="floor"
                    type="number"
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Pricing
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Price *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="e.g., 2000"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <CurrencyInput
                    value={formData.price_currency}
                    onChange={(value) => setFormData({ ...formData, price_currency: value })}
                    className="mt-1"
                  />
                </div>
                {formData.listing_type !== 'sale' && (
                  <div>
                    <Label>Period</Label>
                    <Select
                      value={formData.price_period}
                      onValueChange={(value) => setFormData({ ...formData, price_period: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Per Month</SelectItem>
                        <SelectItem value="week">Per Week</SelectItem>
                        <SelectItem value="day">Per Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {formData.listing_type !== 'sale' && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="deposit">Security Deposit</Label>
                    <Input
                      id="deposit"
                      type="number"
                      value={formData.deposit}
                      onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                      placeholder="e.g., 4000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Deposit Currency</Label>
                    <CurrencyInput
                      value={formData.deposit_currency}
                      onChange={(value) => setFormData({ ...formData, deposit_currency: value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="available_from">Available From</Label>
                    <Input
                      id="available_from"
                      type="date"
                      value={formData.available_from}
                      onChange={(e) => setFormData({ ...formData, available_from: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Amenities */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4">Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {categories.amenities?.map(amenity => (
                  <div key={amenity} className="flex items-center gap-2">
                    <Checkbox
                      id={amenity}
                      checked={formData.amenities.includes(amenity)}
                      onCheckedChange={() => handleAmenityToggle(amenity)}
                    />
                    <Label htmlFor={amenity} className="text-sm cursor-pointer">{amenity}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Expat Features */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Expat-Friendly Features
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 border rounded-xl">
                  <Checkbox
                    id="is_expat_friendly"
                    checked={formData.is_expat_friendly}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_expat_friendly: checked })}
                  />
                  <Label htmlFor="is_expat_friendly" className="cursor-pointer">
                    <div className="font-medium">Expat Friendly</div>
                    <div className="text-sm text-muted-foreground">Landlord welcomes expat tenants</div>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-xl">
                  <Checkbox
                    id="near_international_schools"
                    checked={formData.near_international_schools}
                    onCheckedChange={(checked) => setFormData({ ...formData, near_international_schools: checked })}
                  />
                  <Label htmlFor="near_international_schools" className="cursor-pointer">
                    <div className="font-medium flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-blue-600" />
                      Near International Schools
                    </div>
                    <div className="text-sm text-muted-foreground">Close to international schools</div>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-xl">
                  <Checkbox
                    id="near_expat_areas"
                    checked={formData.near_expat_areas}
                    onCheckedChange={(checked) => setFormData({ ...formData, near_expat_areas: checked })}
                  />
                  <Label htmlFor="near_expat_areas" className="cursor-pointer">
                    <div className="font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4 text-green-600" />
                      Expat-Friendly Area
                    </div>
                    <div className="text-sm text-muted-foreground">Located in popular expat neighborhood</div>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-xl">
                  <Checkbox
                    id="visa_assistance"
                    checked={formData.visa_assistance}
                    onCheckedChange={(checked) => setFormData({ ...formData, visa_assistance: checked })}
                  />
                  <Label htmlFor="visa_assistance" className="cursor-pointer">
                    <div className="font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-600" />
                      Visa Assistance
                    </div>
                    <div className="text-sm text-muted-foreground">Help with visa/residency paperwork</div>
                  </Label>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4">Photos</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img src={img} alt="" className="w-full h-32 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {formData.images.length < 10 && (
                  <label className="border-2 border-dashed border-border rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Add Photo</span>
                    <input
                      type="file"
                      accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Upload up to 10 photos. First photo will be the cover image.</p>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-2xl border-2 border-border p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Your name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="your@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Phone Number</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_whatsapp">WhatsApp Number</Label>
                  <Input
                    id="contact_whatsapp"
                    value={formData.contact_whatsapp}
                    onChange={(e) => setFormData({ ...formData, contact_whatsapp: e.target.value })}
                    placeholder="+1 234 567 8900"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/housing')}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="font-bold rounded-xl px-8"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Saving...' : 'Submitting...'}
                  </>
                ) : (
                  isEditMode ? 'Save Changes' : 'List Property'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CreatePropertyPage;
